import { computed } from 'vue'
import _ from 'lodash'
import {
  is3DMaterialResource,
  isCocosModelAnimationResource,
  isTextureResource,
} from '@/views/creative/utils'
import { ModelAnimation, ModelMaterial } from '@/types/dmaker/category.type'
import { Resource } from '@/types/dmaker/resource.type'
import { AnimationData } from '@/store/creative/model-editor/index.type'

export function useModelStore(store) {
  const resource = computed(() => {
    return _.get(store, 'state.creative.template.templateConfig.resource', {})
  })
  const templateCategory = computed(() => {
    return _.get(store, 'state.creative.template.templateConfig.category')
  })
  const category = computed(() => {
    return _.get(store, 'state.creative.modelEditor.category', {})
  })
  const modelResource = computed(() => {
    return resource.value[category.value.id][0]
  })

  const modelId = computed(() => {
    return _.get(store, 'state.creative.modelEditor.modelId', '')
  })

  const materialId = computed(() => {
    return _.get(store, 'state.creative.modelEditor.materialId', '')
  })

  const animationId = computed<AnimationData['id']>(() => {
    return _.get(store, 'state.creative.modelEditor.animationId', '')
  })

  const animationPath = computed(() => {
    return _.get(store, 'state.creative.modelEditor.animationPath', '')
  })

  const materialPath = computed(() => {
    return _.get(store, 'state.creative.modelEditor.materialPath', '')
  })

  const modelMaterialList = computed(() => {
    return _.get(store, 'state.creative.modelEditor.modelMaterialList', [])
  })

  const modelAnimationList = computed<ModelAnimation[]>(() => {
    return _.get(store, 'state.creative.modelEditor.modelAnimationList', [])
  })

  const replaceMap = computed(() => {
    return _.get(store, 'state.creative.modelEditor.replaceMap', {})
  })

  const targetModelMaterial = computed(() => {
    if (modelMaterialList.value.length) {
      return (
        modelMaterialList.value.find((item: ModelMaterial) => {
          return (
            item.shader_file_path === materialPath.value &&
            JSON.stringify(item.id) === JSON.stringify(materialId.value)
          )
        }) || {}
      )
    }
    return {}
  })

  const hsvModel = computed(() => {
    return store.state.creative.modelEditor.hsvModel
  })
  const storeBasicModel = computed(() => {
    return store.state.creative.modelEditor.basicModel
  })

  const shaderModel = computed(() => {
    return store.state.creative.modelEditor.shaderModel
  })

  const animationModel = computed<Partial<AnimationData>>(() => {
    return store.state.creative.modelEditor.animationModel
  })

  const resetModel = computed(() => {
    return store.state.creative.modelEditor.resetModel
  })

  const replaceData = computed(() => {
    return store.state.creative.modelEditor.replaceData
  })

  const getResourceListByFilter = (
    filter: (res: Resource) => boolean,
  ): Resource[] => {
    const resList: Resource[] = []
    if (!_.isEmpty(resource.value)) {
      _.each(resource.value, (value) => {
        if (value.some((res) => filter(res))) {
          resList.push(...value)
        }
      })
    }
    return resList
  }

  const cocosActionResourceList = computed<Resource[]>(() => {
    return getResourceListByFilter(isCocosModelAnimationResource)
  })

  const textureResourceList = computed<Resource[]>(() => {
    return getResourceListByFilter(isTextureResource)
  })

  const shaderResourceList = computed<Resource[]>(() => {
    return getResourceListByFilter(is3DMaterialResource)
  })

  return {
    modelId,
    materialId,
    animationId,
    hsvModel,
    storeBasicModel,
    shaderModel,
    category,
    resource,
    modelMaterialList,
    targetModelMaterial,
    materialPath,
    cocosActionResourceList,
    textureResourceList,
    shaderResourceList,
    modelAnimationList,
    animationPath,
    templateCategory,
    animationModel,
    replaceMap,
    resetModel,
    replaceData,
  }
}

export enum TabType {
  Basic = 'BASIC',
  Appearance = 'APPEARANCE',
  Animation = 'ANIMATION',
}
